import type { FC, ReactNode } from "react";
import { useRef } from "react";

import { useHorizontalSwipe } from "@ui/hooks/useHorizontalSwipe/useHorizontalSwipe";

import { CategoryFilterItem } from "./CategoryFilterItem";

import styles from "./CategoryFilter.module.scss";

type PrizeLocatorFilterItem = {
  label: string;
  categoryKey: string;
  iconUrl: string;
  isActiveFilter?: boolean;
  isDisabled?: boolean;
};

type StoreLocatorFilter = PrizeLocatorFilterItem[];

const getActiveItemIndex = (key: string, activeFilters: StoreLocatorFilter) =>
  activeFilters?.findIndex(({ categoryKey }) => categoryKey === key);

export type CategoryFilterProps = {
  categoryFilterTitle?: string;
  filters?: StoreLocatorFilter;
  activeFilters?: StoreLocatorFilter;
  onFilterClick: (key: string) => void;
  additionalFiltersSlot?: ReactNode;
};

const getFiltersWithStatus = (filters: StoreLocatorFilter, activeFilters: StoreLocatorFilter) =>
  filters.map((filter) => {
    const isActive = getActiveItemIndex(filter.categoryKey, activeFilters) !== -1;
    if (isActive) {
      return { ...filter, isActiveFilter: true };
    }
    return filter;
  });

export const CategoryFilter: FC<CategoryFilterProps> = ({
  onFilterClick,
  activeFilters,
  filters,
  categoryFilterTitle,
  additionalFiltersSlot,
}) => {
  const ulRef = useRef<HTMLUListElement>(null);
  const { onTouchEnd, onTouchMove, onTouchStart, elementPositionX } = useHorizontalSwipe(ulRef);

  const filtersWithStatus = getFiltersWithStatus(filters ?? [], activeFilters ?? []);

  if (filtersWithStatus?.length < 1) return null;

  return (
    <div
      className={styles.categoryFilterContainer}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      {categoryFilterTitle && <h4 className={styles.categoryFilterTitle}>{categoryFilterTitle}</h4>}
      <div className={styles.filtersWrapper}>
        <ul style={{ transform: `translateX(${elementPositionX}px)` }} className={styles.list} ref={ulRef}>
          {filtersWithStatus.map(({ categoryKey, iconUrl, label, isActiveFilter, isDisabled }) => {
            if (isDisabled) {
              return null;
            }

            return (
              <CategoryFilterItem
                key={categoryKey}
                categoryKey={categoryKey}
                iconUrl={iconUrl}
                label={label}
                onFilterClick={onFilterClick}
                isActiveFilter={isActiveFilter}
              />
            );
          })}
        </ul>
        {additionalFiltersSlot}
      </div>
    </div>
  );
};
